import { useRef } from 'react';
import { IMPORT_FILE_CHANGE, IMPORT_FILE_CHOOSE, importFileSize } from '../../lib/copy';
import './file-picker.css';

/**
 * **The file picker, in this app's control grammar** (ui-design §5).
 *
 * It was a bare `<input type="file">`, which is the one control a browser draws
 * entirely itself: a grey system button in the platform's own face, a
 * "No file chosen" in the platform's own words, and neither of them reachable by
 * a stylesheet. Beside the ivory fields and the tracked-caps buttons on the same
 * modal it read as a piece of a different program.
 *
 * So the input is still the input — nothing else opens a file dialog, and
 * nothing else is a file control to a screen reader — and it is moved out of
 * sight rather than out of the page:
 *
 *  - the caller's `<label for>` still points at it, so its accessible name is
 *    the field's own label;
 *  - `tabIndex={-1}` keeps it out of the tab order, because a control nobody can
 *    see should not be a stop nobody can find. The button is the stop;
 *  - the button forwards the click, and says which of the two gestures it is:
 *    choosing a file the first time, changing it after.
 *
 * The chosen file is named beside the button rather than inside it — a button
 * whose words are a filename is a button whose words move — and the button
 * points at that line, so pressing Tab reads "Change, button, board.xlsx · 2.1 MB".
 */
export function FilePicker({
  id,
  accept,
  disabled,
  chosen,
  hint,
  onChoose,
}: {
  /** The id the caller's `<label htmlFor>` names. */
  id: string;
  accept: string;
  disabled: boolean;
  chosen: File | null;
  /** What stands where the filename will go while there is no file. */
  hint: string;
  onChoose: (file: File | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const saidId = `${id}-chosen`;

  return (
    <div className="file-picker">
      <input
        id={id}
        className="visually-hidden"
        type="file"
        tabIndex={-1}
        ref={input}
        accept={accept}
        disabled={disabled}
        onChange={(event) => onChoose(event.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        className="btn act"
        disabled={disabled}
        aria-describedby={saidId}
        onClick={() => input.current?.click()}
      >
        {chosen ? IMPORT_FILE_CHANGE : IMPORT_FILE_CHOOSE}
      </button>
      <span className={chosen ? 'file-chosen' : 'file-chosen none'} id={saidId}>
        {chosen ? `${chosen.name} · ${importFileSize(chosen.size)}` : hint}
      </span>
    </div>
  );
}
